import { getHighlightClasses } from "@/lib/get-tokens-highlights";
import { cn } from "../lib/utils";
import { useMemo } from "react";

// WARNING
// new token colors or classes require `npm run build` or `npm run gen-colors`
// this is a tailwind dynamic class issue
// https://tailwindcss.com/docs/detecting-classes-in-source-files#safelisting-classes

interface TokenProps {
  id?: string;
  variant?: "xs" | "sm" | "md" | "lg" | "3xl";
  shadowVariant?: "xs" | "sm" | "md" | "lg" | "3xl";
  hq?: boolean;
  leftAssetId?: number;
  rightAssetId?: number;
  className?: string;
  glowing?: boolean;
}

const borderSizeMap = {
  xs: "",
  sm: "",
  md: "",
  lg: "border-2",
  xl: "border-4",
  "3xl": "outline outline-[16px]",
};

const sizeMap = {
  xs: {
    size: 32,
    shadowSize: "shadow-[0_0_15px_5px]",
  },
  sm: {
    size: 64,
    shadowSize: "shadow-[0_0_20px_5px]",
  },
  md: {
    size: 96,
    shadowSize: "shadow-[0_0_25px_5px]",
  },
  lg: {
    size: 128,
    shadowSize: "shadow-[0_0_30px_5px]",
  },
  xl: {
    size: 256,
    shadowSize: "shadow-[0_0_40px_5px]",
  },
  "3xl": {
    size: 992,
    shadowSize: "shadow-[0_0_40px_5px]",
  },
};

export function Token({
  id,
  variant = "md",
  shadowVariant,
  leftAssetId = 0, // 0 is ALGO
  rightAssetId = 0,
  className = "",
  glowing = true,
  hq,
}: TokenProps) {
  const { size } = sizeMap[variant];
  const { shadowSize } = sizeMap[shadowVariant ?? variant];
  const borderSize = borderSizeMap[variant];

  const leftImageUrl = hq
    ? `/assets/ds-token-icons/${leftAssetId}-hq.png`
    : `/assets/ds-token-icons/${leftAssetId}.png`;
  const rightImageUrl = hq
    ? `/assets/ds-token-icons/${rightAssetId}-hq.png`
    : `/assets/ds-token-icons/${rightAssetId}.png`;

  const bgClass = getHighlightClasses(leftAssetId, "bg");
  const shadowClass = getHighlightClasses(leftAssetId, "shadow", "/70");
  const borderClass = useMemo(
    () =>
      getHighlightClasses(leftAssetId, "border") +
      " " +
      getHighlightClasses(leftAssetId, "outline") +
      " " +
      borderSize,
    [leftAssetId]
  );
  
  return (
    <div
      className={cn("relative", className)}
      style={{ width: size, height: size }}
      id={id}
    >
      <div
        className={cn(
          "absolute -inset-[1px] rounded-full",
          // `group-hover:${shadowSize}`, //  TODO not working? listing card has group on top
          // `group-hover:${shadowClass}`,
          glowing ? shadowSize : "",
          glowing ? shadowClass : "",
          glowing && "animate-[pulse_5s_linear_infinite]",
          bgClass
        )}
      />
      <div
        className={cn(
          `relative rounded-full overflow-hidden border`,
          borderClass
        )}
        style={{ width: size, height: size }}
      >
        {/* Left half */}
        <div className={`absolute inset-0 ${bgClass}`}>
          <div
            className="w-full h-full"
            style={{
              backgroundImage: `url(${leftImageUrl})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        </div>
        {/* Right half */}
        <div className="absolute -inset-[1px]">
          <svg
            width="0"
            height="0"
            style={{ position: "absolute", zIndex: -1 }}
          >
            <defs>
              <clipPath id="curvedClip" clipPathUnits="objectBoundingBox">
                <path d="M1,0.5c0,0.275-0.222,0.499-0.497,0.5H0.5C0.36,1,0.25,0.89,0.25,0.75c0-0.138,0.112-0.25,0.25-0.25c0.138,0,0.25-0.112,0.25-0.25c0-0.138-0.112-0.25-0.25-0.25C0.778,0.001,1,0.225,1,0.5z" />
              </clipPath>
            </defs>
          </svg>
          <div className="w-full h-full relative">
            <div
              className="w-full h-full"
              style={{
                clipPath: "url(#curvedClip)",
                backgroundImage: `url(${rightImageUrl})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
            <div
              className={`absolute inset-0 ${bgClass} opacity-45`}
              style={{
                clipPath: "url(#curvedClip)",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
